import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, Crown, Sparkles } from "lucide-react";

const TIERS = [
  {
    key: "community_monthly",
    name: "Community · Monthly",
    amount: "0.99",
    cadence: "per month",
    icon: Sparkles,
    bullets: [
      "Edit your community profile",
      "Connect with other PFC members",
      "Post to the feed (public / connections / private)",
      "Send direct messages with image attachments",
    ],
  },
  {
    key: "community_yearly",
    name: "Community · Yearly",
    amount: "9.99",
    cadence: "per year",
    icon: Crown,
    bullets: [
      "Everything in Monthly",
      "Save vs paying monthly",
      "Best for active collaborators & artists",
      "Lock in 12 months of community access",
    ],
    highlight: true,
  },
];

export default function CommunityJoin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/community/me/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, [user]);

  const checkout = async (key) => {
    if (!user) { nav("/login"); return; }
    setLoadingKey(key);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: key,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoadingKey(null);
    }
  };

  return (
    <div data-testid="community-join-page">
      <section className="relative py-16 border-b border-white/5 splatter-blue grain">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="label text-pfc-gold mb-3">PFC Community Membership</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white uppercase leading-[0.9]">
            Join the <span className="gold-text">Collaboration</span><br />
            <span className="silver-text">Community.</span>
          </h1>
          <p className="text-white/65 mt-5 font-body text-lg max-w-2xl">
            Unlock profile editing, connections, direct messaging, and posting on the PFC community feed.
            Just <strong className="text-white">$0.99/month</strong> or <strong className="text-white">$9.99/year</strong>.
          </p>
        </div>
      </section>

      {user && sub?.is_active && (
        <section className="bg-pfc-gold/10 border-b border-pfc-gold/30">
          <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-6 flex items-center justify-between gap-3 flex-wrap" data-testid="join-already-active">
            <div>
              <div className="label text-pfc-gold mb-1">You're In</div>
              <p className="font-display text-white text-xl">
                Active subscription · {sub.plan === "community_yearly" ? "Yearly" : sub.plan === "community_monthly" ? "Monthly" : "Admin"}
              </p>
              <p className="text-white/60 text-sm font-body mt-1">
                {sub.expires_at ? `Renews / expires: ${new Date(sub.expires_at).toLocaleDateString()}` : "Lifetime"}
              </p>
            </div>
            <Link to="/community" className="font-sub uppercase tracking-[0.25em] text-xs px-5 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition">
              Open Community
            </Link>
          </div>
        </section>
      )}

      <section className="max-w-[1100px] mx-auto px-5 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="community-tiers">
          {TIERS.map((t) => {
            const Icon = t.icon;
            return (
              <div
                key={t.key}
                className={`pfc-card relative p-8 lg:p-10 flex flex-col ${t.highlight ? "md:scale-105 border-pfc-gold/50" : ""}`}
                data-testid={`community-tier-${t.key}`}
              >
                {t.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 label bg-pfc-gold text-ink-900 px-3 py-1.5">
                    Best Value
                  </span>
                )}
                <Icon className="text-pfc-gold mb-5" size={32} />
                <h3 className="font-display text-2xl text-white uppercase leading-tight">{t.name}</h3>

                <div className="mt-7 mb-8">
                  <span className="font-display text-6xl gold-text">${t.amount}</span>
                  <span className="font-sub uppercase tracking-[0.2em] text-xs text-white/50 ml-2">USD · {t.cadence}</span>
                </div>

                <ul className="space-y-3 mb-10 flex-1">
                  {t.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-white/80 font-body text-sm">
                      <Check className="text-pfc-gold shrink-0 mt-0.5" size={16} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => checkout(t.key)}
                  disabled={loadingKey === t.key}
                  className={`w-full font-sub uppercase tracking-[0.25em] text-sm py-4 transition disabled:opacity-50 ${
                    t.highlight
                      ? "bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright"
                      : "border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold"
                  }`}
                  data-testid={`join-${t.key}`}
                >
                  {loadingKey === t.key ? "Redirecting…" : `Subscribe — $${t.amount}/${t.cadence.includes("year") ? "yr" : "mo"}`}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center" data-testid="community-join-footnote">
          <p className="text-white/55 font-body max-w-xl mx-auto">
            One-time charge via Stripe. Renew manually anytime. Cancel by not renewing — your profile stays visible but write features lock.
          </p>
        </div>
      </section>
    </div>
  );
}
